import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Crown,
  GraduationCap,
  Heart,
  Lightbulb,
  Puzzle,
  ShieldCheck,
  Smile,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";

const KID_BOY = "/landing/hero/boy.webp";
const KID_GIRL = "/landing/hero/girl.webp";
// Pudar lembut di tepi supaya tiada garisan keras pada imej
const KID_FADE = "radial-gradient(ellipse 52% 62% at 50% 42%, #000 62%, transparent 100%)";
const KID_MASK = { WebkitMaskImage: KID_FADE, maskImage: KID_FADE };

/* Count-up bila kelihatan pada skrin */
function useCountUp(target, duration = 1600) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let raf;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min(1, (now - t0) / duration);
          setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [target, duration]);
  return [ref, val];
}

/* Garis swoosh emas bawah tulisan skrip */
const GoldSwoosh = ({ className = "" }) => (
  <svg viewBox="0 0 300 24" className={className} preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id="pmmSwoosh" x1="0" x2="1">
        <stop offset="0" stopColor="#f0a500" stopOpacity="0" />
        <stop offset="0.25" stopColor="#fcbf1e" />
        <stop offset="1" stopColor="#f7931e" />
      </linearGradient>
    </defs>
    <path d="M4 18 C 90 4, 200 2, 296 10 C 210 10, 110 14, 4 22 Z" fill="url(#pmmSwoosh)" />
  </svg>
);

export default function PmmHero({ ms, cms = {}, logo }) {
  const [studentsRef, students] = useCountUp(20000);

  const pillars = [
    { icon: BookOpen, tone: "red", t: ms ? "Membaca" : "Reading", s: ms ? "Jambatan Ilmu" : "The Bridge to Knowledge" },
    { icon: Lightbulb, tone: "navy", t: ms ? "Mengubah" : "Transforms", s: ms ? "Minda dan Masa Depan" : "Minds and the Future" },
    { icon: Users, tone: "red", t: ms ? "Bersama" : "Together", s: ms ? "Membentuk Generasi Lebih Baik" : "Shaping a Better Generation" },
    { icon: Target, tone: "navy", t: ms ? "Mesti Lancar Baca" : "Must Read Fluently", s: "" },
  ];

  const why = [
    { icon: Puzzle, t: ms ? "Sistematik" : "Systematic", s: ms ? "Dan Mudah Difahami" : "And Easy to Understand" },
    { icon: Target, t: ms ? "Teknik Mengeja" : "Spelling Technique", s: "Step By Step" },
    { icon: BarChart3, t: ms ? "Bertahap" : "Levelled", s: ms ? "Mengikut Tahap Umur" : "By Age Level" },
    { icon: Heart, t: ms ? "Menyeronokkan" : "Fun", s: ms ? "Anak Lebih Bermotivasi" : "More Motivated Kids" },
  ];

  const books = [
    { t: ms ? "Kenal Huruf" : "Letters", c: "from-navy-700 to-navy-500", w: "w-[88%]" },
    { t: ms ? "Suku Kata" : "Syllables", c: "from-orange-600 to-orange-400", w: "w-[80%]" },
    { t: ms ? "Mengeja" : "Spelling", c: "from-yellow-500 to-yellow-300", w: "w-[92%]", dark: true },
    { t: ms ? "Membaca" : "Reading", c: "from-emerald-700 to-emerald-500", w: "w-[84%]" },
    { t: ms ? "Keyakinan Diri" : "Confidence", c: "from-purple-700 to-purple-500", w: "w-full" },
  ];

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-[#f3f8ff] to-[#e6f0ff]" data-testid="landing-hero-pmm">
      {/* Hiasan swoosh atas (oren kiri, biru kanan) */}
      <div className="pointer-events-none absolute hidden md:block -left-24 -top-24 h-72 w-[28rem] -rotate-12 rounded-[50%] bg-gradient-to-r from-orange-700 to-orange-500 opacity-90 shadow-2xl" />
      <div className="pointer-events-none absolute hidden md:block -left-24 top-36 h-2 w-[30rem] -rotate-12 bg-gradient-to-r from-yellow-500 via-yellow-300 to-transparent" />
      <div className="pointer-events-none absolute hidden md:block -right-32 -top-28 h-64 w-[30rem] rotate-6 rounded-[50%] bg-gradient-to-l from-navy-900 to-navy-700 opacity-90" />
      <div className="pointer-events-none absolute hidden md:block -right-24 top-28 h-1.5 w-[26rem] rotate-6 bg-gradient-to-l from-yellow-400 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-orange-700 via-yellow-400 to-navy-800 md:hidden" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#e97a0c 1px, transparent 1px)", backgroundSize: "22px 22px" }} />

      <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-8 sm:px-6 md:pt-12">
        {/* Skrip kanan atas */}
        <div className="pmm-pop absolute right-4 top-44 z-10 hidden w-48 rotate-[-6deg] text-right lg:block" style={{ animationDelay: "0.4s" }} data-testid="hero-script-tagline">
          <div className="font-script text-3xl font-bold leading-[1.05] text-navy-900">
            {ms ? <>Membaca<br />Mengubah<br />Masa Depan</> : <>Reading<br />Changes<br />The Future</>}
          </div>
          <GoldSwoosh className="ml-auto mt-1 h-4 w-40" />
        </div>

        <div className="grid items-end gap-6 lg:grid-cols-[1fr_minmax(0,40rem)_1fr]">
          {/* Budak lelaki (desktop) */}
          <div className="relative hidden lg:block">
            <div className="pmm-float relative">
              <div className="absolute inset-x-6 bottom-6 top-20 rounded-full bg-orange-500/15 blur-3xl" />
              <img src={KID_BOY} alt={ms ? "Murid PMM gembira" : "Happy PMM pupil"} className="relative w-full max-w-[20rem] drop-shadow-2xl" style={KID_MASK} data-testid="hero-kid-boy" />
            </div>
          </div>

          {/* Kandungan tengah */}
          <div className="text-center">
            {/* Lambang */}
            <div className="pmm-pop mx-auto mb-4 w-fit" data-testid="hero-emblem">
              <div className="rounded-[1.6rem] bg-gradient-to-br from-yellow-200 via-yellow-500 to-yellow-700 p-1 shadow-xl shadow-yellow-600/30">
                <img src={logo} alt="Logo PMM" className="h-20 w-20 rounded-[1.35rem] object-cover sm:h-24 sm:w-24" />
              </div>
            </div>

            <h1 className="pmm-pop font-display font-extrabold uppercase leading-[0.95] tracking-tight" style={{ animationDelay: "0.1s" }} data-testid="hero-title">
              <span className="block text-[2.6rem] text-navy-900 sm:text-6xl lg:text-7xl">{cms.hero_title || (ms ? "Program" : "PMM Reading")}</span>
              <span
                className="mt-1 block bg-clip-text pb-1 text-[2.6rem] text-transparent drop-shadow-[0_3px_0_rgba(240,165,0,0.55)] sm:text-6xl lg:text-7xl"
                style={{ backgroundImage: "linear-gradient(180deg,#ffb870 0%,#f7931e 45%,#e97a0c 100%)", WebkitBackgroundClip: "text" }}
              >
                {cms.hero_highlight || (ms ? "Membaca PMM" : "Program")}
              </span>
            </h1>

            {/* Reben navy */}
            <div className="pmm-pop relative mx-auto mt-4 w-fit" style={{ animationDelay: "0.2s" }}>
              <div className="absolute -inset-x-4 inset-y-1.5 -z-10 bg-navy-950" style={{ clipPath: "polygon(0 0,100% 0,96% 50%,100% 100%,0 100%,4% 50%)" }} />
              <div className="rounded-md border-2 border-yellow-400 bg-gradient-to-r from-navy-900 via-navy-700 to-navy-900 px-6 py-2 font-display text-xl font-extrabold uppercase tracking-wide text-white shadow-xl sm:px-10 sm:text-3xl" data-testid="hero-ribbon">
                {cms.hero_badge || (ms ? "Mesti Lancar Baca" : "Must Read Fluently")}
              </div>
            </div>

            <div className="pmm-pop mx-auto mt-4 w-fit" style={{ animationDelay: "0.3s" }}>
              <div className="font-script text-2xl font-bold text-navy-900 sm:text-3xl">{ms ? "Ilmu Bermula Dengan Membaca" : "Knowledge Begins With Reading"}</div>
              <GoldSwoosh className="mx-auto h-3 w-56 sm:w-72" />
            </div>

            {cms.hero_subtitle && (
              <p className="mx-auto mt-3 max-w-xl text-base font-semibold leading-relaxed text-navy-900/75" data-testid="hero-subtitle">{cms.hero_subtitle}</p>
            )}

            {/* 4 tonggak */}
            <div className="mt-7 grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-4" data-testid="hero-pillars">
              {pillars.map((p, i) => (
                <div key={p.t} className="pmm-pop group flex flex-col items-center" style={{ animationDelay: `${0.35 + i * 0.1}s` }} data-testid={`hero-pillar-${i}`}>
                  <div className={`grid h-20 w-20 place-items-center rounded-full border-4 border-yellow-400 shadow-xl transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-105 sm:h-24 sm:w-24 ${p.tone === "red" ? "bg-gradient-to-br from-orange-500 to-orange-800 shadow-orange-700/30" : "bg-gradient-to-br from-navy-600 to-navy-900 shadow-navy-900/30"}`}>
                    <p.icon className="h-9 w-9 text-white sm:h-11 sm:w-11" strokeWidth={2.2} />
                  </div>
                  <div className="mt-2 font-display text-base font-extrabold uppercase leading-tight text-navy-900 sm:text-lg">{p.t}</div>
                  {p.s ? (
                    <div className="mt-0.5 text-[11px] font-bold uppercase leading-tight text-navy-900/70 sm:text-xs">{p.s}</div>
                  ) : (
                    <GoldSwoosh className="mt-1 h-2 w-20" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Budak perempuan (desktop) */}
          <div className="relative hidden lg:block">
            <div className="pmm-float-delayed relative ml-auto max-w-[20rem]">
              <div className="absolute inset-x-6 bottom-6 top-20 rounded-full bg-navy-500/15 blur-3xl" />
              <img src={cms.hero_image || KID_GIRL} alt={ms ? "Murid PMM boleh membaca" : "PMM pupil can read"} className="relative w-full drop-shadow-2xl" style={KID_MASK} data-testid="hero-kid-girl" />
            </div>
          </div>
        </div>

        {/* Kanak-kanak (mobile/tablet) */}
        <div className="mt-6 grid grid-cols-2 items-end gap-2 lg:hidden" data-testid="hero-kids-mobile">
          <img src={KID_BOY} alt="" className="pmm-float w-full drop-shadow-xl" style={KID_MASK} />
          <img src={cms.hero_image || KID_GIRL} alt="" className="pmm-float-delayed w-full drop-shadow-xl" style={KID_MASK} />
        </div>
      </div>

      {/* Bar statistik navy */}
      <div className="relative bg-gradient-to-r from-navy-950 via-navy-800 to-navy-950 shadow-2xl" data-testid="hero-stats-bar">
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
        <div className="mx-auto grid max-w-7xl grid-cols-2 divide-yellow-400/30 px-4 py-5 sm:px-6 lg:grid-cols-4 lg:divide-x">
          {[
            { icon: Users, top: ms ? "Sejak Tahun" : "Since", big: "2007", bot: "" },
            { icon: GraduationCap, top: ms ? "Telah Bantu Lebih" : "Helped More Than", big: <span ref={studentsRef} className="tabular-nums">{students.toLocaleString("en-US")}</span>, bot: ms ? "Pelajar" : "Students" },
            { icon: Smile, top: ms ? "Khas Untuk Umur" : "Specially For Ages", big: "4 - 9", bot: ms ? "Tahun" : "Years" },
            { icon: TrendingUp, top: ms ? "Teknik Sistematik" : "Systematic &", big: null, bot: ms ? "Dan Terbukti Berkesan" : "Proven Technique" },
          ].map((s, i) => (
            <div key={i} className="flex items-center justify-center gap-3 px-3 py-3" data-testid={`hero-stat-${i}`}>
              <s.icon className="h-9 w-9 shrink-0 text-yellow-400 sm:h-11 sm:w-11" />
              <div className="leading-tight text-white">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/85 sm:text-xs">{s.top}</div>
                {s.big && (
                  <div className="font-display text-2xl font-extrabold text-transparent sm:text-4xl" style={{ backgroundImage: "linear-gradient(180deg,#f5e08d,#f0a500)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>{s.big}</div>
                )}
                {s.bot && <div className={`font-display text-xs font-extrabold uppercase sm:text-sm ${s.big ? "text-white" : "text-yellow-400"}`}>{s.bot}</div>}
              </div>
            </div>
          ))}
        </div>
        <div className="h-1 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
      </div>

      {/* Kenapa pilih PMM */}
      <div className="relative mx-auto max-w-6xl px-4 pt-12 sm:px-6" data-testid="hero-why">
        <h2 className="text-center font-display text-2xl font-extrabold uppercase tracking-tight text-navy-900 sm:text-4xl">
          {ms ? "Kenapa Pilih " : "Why Choose "}
          <span className="text-orange-600">{ms ? "Program Membaca PMM?" : "The PMM Reading Program?"}</span>
        </h2>
        <div className="mt-7 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
          {why.map((w, i) => (
            <div key={w.t} className="pmm-pop group rounded-[1.5rem] border border-orange-500/10 bg-white p-5 text-center shadow-lg shadow-orange-900/5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-orange-900/10" style={{ animationDelay: `${i * 0.1}s` }} data-testid={`hero-why-${i}`}>
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-orange-50 transition-colors duration-300 group-hover:bg-orange-600">
                <w.icon className="h-8 w-8 text-orange-600 transition-colors duration-300 group-hover:text-white" strokeWidth={2.4} />
              </div>
              <div className="mt-3 font-display text-base font-extrabold uppercase text-navy-900 sm:text-lg">{w.t}</div>
              <div className="text-sm font-semibold text-navy-900/70">{w.s}</div>
            </div>
          ))}
        </div>

        {/* CTA besar */}
        <div className="relative mx-auto mt-10 flex w-fit flex-col items-center">
          <span className="pointer-events-none absolute -left-8 top-1/2 hidden -translate-y-1/2 space-y-2 sm:block" aria-hidden="true">
            <span className="block h-1 w-5 -rotate-[25deg] rounded bg-orange-500" /><span className="block h-1 w-6 rounded bg-orange-500" /><span className="block h-1 w-5 rotate-[25deg] rounded bg-orange-500" />
          </span>
          <span className="pointer-events-none absolute -right-8 top-1/2 hidden -translate-y-1/2 space-y-2 sm:block" aria-hidden="true">
            <span className="block h-1 w-5 rotate-[25deg] rounded bg-orange-500" /><span className="block h-1 w-6 rounded bg-orange-500" /><span className="block h-1 w-5 -rotate-[25deg] rounded bg-orange-500" />
          </span>
          <Link
            to="/register"
            className="pmm-atau group flex items-center gap-4 rounded-full border-2 border-yellow-300/70 bg-gradient-to-b from-orange-500 to-orange-800 py-3 pl-3 pr-8 text-left text-white shadow-2xl shadow-orange-700/40 transition-transform duration-300 hover:scale-105 sm:pr-12"
            data-testid="landing-cta-register"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-orange-600 shadow-md transition-transform duration-300 group-hover:translate-x-1 sm:h-14 sm:w-14">
              <ArrowRight className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={3} />
            </span>
            <span className="leading-tight">
              <span className="block font-display text-2xl font-extrabold uppercase tracking-wide sm:text-3xl">{ms ? "Daftar Sekarang" : "Register Now"}</span>
              <span className="block text-sm font-semibold text-white/90 sm:text-base">{ms ? "Jom Bantu Anak Lancar Membaca!" : "Let's Help Your Child Read Fluently!"}</span>
            </span>
          </Link>
          <Link to="/login" className="mt-4 text-sm font-bold text-navy-800 underline-offset-4 transition-colors hover:text-orange-600 hover:underline" data-testid="landing-cta-login">
            {ms ? "Sudah berdaftar? Log masuk aplikasi" : "Already registered? Sign in to the app"}
          </Link>
        </div>

        {/* Buku bertingkat + skrip + pingat */}
        <div className="mt-8 grid items-end gap-6 pb-6 md:grid-cols-[14rem_1fr_11rem]">
          <div className="mx-auto w-56 space-y-1 md:mx-0" data-testid="hero-books">
            {books.map((b, i) => (
              <div key={b.t} className={`pmm-pop ${b.w} rounded-r-lg rounded-l-sm border-l-8 border-black/15 bg-gradient-to-r ${b.c} px-4 py-2 font-display text-sm font-extrabold uppercase tracking-wide shadow-md transition-transform duration-300 hover:translate-x-2 ${b.dark ? "text-navy-950" : "text-white"}`} style={{ animationDelay: `${0.1 * i}s` }}>
                {b.t}
              </div>
            ))}
          </div>
          <div className="text-center">
            <div className="font-script text-3xl font-bold leading-tight text-navy-900 sm:text-4xl">
              {ms ? <>Anak Lancar Membaca<br />Masa Depan Lebih Cerah</> : <>Fluent Readers<br />Brighter Futures</>}
            </div>
            <GoldSwoosh className="mx-auto mt-1 h-3 w-64" />
          </div>
          <div className="pmm-float mx-auto flex flex-col items-center" data-testid="hero-medal">
            <div className="grid h-36 w-36 place-items-center rounded-full bg-gradient-to-br from-yellow-200 via-yellow-500 to-yellow-700 p-1.5 shadow-2xl shadow-yellow-700/40">
              <div className="grid h-full w-full place-items-center rounded-full border-2 border-yellow-300 bg-gradient-to-b from-navy-800 to-navy-950 text-center">
                <div>
                  <Crown className="mx-auto h-5 w-5 fill-yellow-400 text-yellow-400" />
                  <div className="mt-0.5 font-display text-[10px] font-extrabold uppercase leading-tight tracking-wider text-white">{ms ? <>Pilihan Ibu Bapa<br />Sejak</> : <>Parents' Choice<br />Since</>}</div>
                  <div className="font-display text-2xl font-extrabold text-yellow-400">2007</div>
                </div>
              </div>
            </div>
            <div className="-mt-3 flex gap-6">
              <span className="h-8 w-5 bg-gradient-to-b from-yellow-500 to-yellow-700" style={{ clipPath: "polygon(0 0,100% 0,100% 100%,50% 75%,0 100%)" }} />
              <span className="h-8 w-5 bg-gradient-to-b from-orange-600 to-orange-800" style={{ clipPath: "polygon(0 0,100% 0,100% 100%,50% 75%,0 100%)" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Jalur bawah */}
      <div className="relative bg-gradient-to-r from-orange-800 via-orange-700 to-orange-800" data-testid="hero-bottom-strip">
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-around gap-3 px-4 py-4 text-sm font-bold text-white sm:flex-row sm:px-6">
          <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-yellow-300" /> {ms ? "Program Terbukti Berkesan" : "Proven Effective Program"}</span>
          <span className="flex items-center gap-2"><Users className="h-5 w-5 text-yellow-300" /> {ms ? "Bersama Membentuk Generasi Lebih Baik" : "Together Shaping a Better Generation"}</span>
          <span className="flex items-center gap-2"><Heart className="h-5 w-5 fill-yellow-300 text-yellow-300" /> {ms ? "Membaca Mengubah Masa Depan" : "Reading Changes the Future"}</span>
        </div>
      </div>
    </section>
  );
}
